import HCaptcha from './LazyHCaptcha'
import { useContactForm } from '../hooks/useContactForm'
import { IconArrowRight, IconCheck } from './ui/Icon'

const HCAPTCHA_SITE_KEY = import.meta.env.VITE_HCAPTCHA_SITE_KEY

/**
 * Single text/email/tel field with label association, a11y wiring, and inline
 * error description.
 *
 * @param {Object} props
 * @param {string} props.id - Input id (also drives htmlFor + aria-describedby).
 * @param {string} props.type
 * @param {string} props.label
 * @param {string} props.placeholder
 * @param {string} props.value
 * @param {boolean} props.required
 * @param {string} [props.autoComplete]
 * @param {boolean} props.disabled
 * @param {boolean} props.invalid
 * @param {string} props.errorText
 * @param {(value: string) => void} props.onChange
 * @param {boolean} [props.multiline]
 */
function Field({
  id,
  type,
  label,
  placeholder,
  value,
  required,
  autoComplete,
  disabled,
  invalid,
  errorText,
  onChange,
  multiline = false,
}) {
  const describedBy = invalid ? `${id}-error` : undefined
  const shared = {
    id,
    value,
    placeholder,
    disabled,
    required: required || undefined,
    'aria-required': required || undefined,
    'aria-invalid': invalid || undefined,
    'aria-describedby': describedBy,
    autoComplete,
    onChange: (e) => onChange(e.target.value),
  }
  return (
    <div className="form-group">
      <label htmlFor={id}>{label}</label>
      {multiline ? (
        <textarea {...shared}></textarea>
      ) : (
        <input type={type} {...shared} />
      )}
      {invalid && (
        <span id={describedBy} className="form-field-error" role="alert">
          {errorText}
        </span>
      )}
    </div>
  )
}

/**
 * Contact / RFQ form. Presentational shell driven by useContactForm; preserves
 * the legacy CSS class names so existing styling applies unchanged.
 *
 * @param {Object} [props]
 * @param {'tr'|'en'|'de'} [props.lang='tr']
 * @param {string} [props.subjectPrefix=''] - Email subject prefix (service name).
 * @param {string} [props.service='general'] - Analytics service dimension.
 * @param {string} [props.formLocation='home_contact'] - Analytics form_location dimension.
 * @param {'home'|'service'} [props.variant='home'] - 'service' adds the service-form class.
 */
function ContactForm({
  lang = 'tr',
  subjectPrefix = '',
  service = 'general',
  formLocation = 'home_contact',
  variant = 'home',
}) {
  const {
    formData,
    setField,
    formStatus,
    errors,
    captchaRef,
    onCaptchaVerify,
    onCaptchaExpire,
    honeypotProps,
    handleSubmit,
    reset,
    t,
  } = useContactForm({ lang, subjectPrefix, service, formLocation })

  const isSending = formStatus === 'sending'
  const captchaInvalid = Boolean(errors.captcha)
  const captchaGroupLabelId = 'cf-captcha-label'
  const hasFieldErrors = Object.keys(errors).some(
    (key) => key !== 'config' && Boolean(errors[key])
  )
  const showFormError = formStatus === 'error' && (Boolean(errors.config) || !hasFieldErrors)

  return (
    <div className={`contact-form-wrapper ${formStatus}`}>

      {isSending && (
        <div className="form-loading-overlay">
          <div className="diagonal-light"></div>
        </div>
      )}

      <div role="status" aria-live="polite" className="form-status">
        {showFormError && <p className="form-error">{t.messages.errorGeneric}</p>}
      </div>

      {formStatus === 'success' ? (
        <div className="form-success-message">
          <div className="success-icon">
            <IconCheck />
          </div>
          <h3>{t.messages.successTitle}</h3>
          <p>{t.messages.successBody}</p>
          <button className="form-submit success-btn" onClick={reset}>
            {t.messages.successAgain}
          </button>
        </div>
      ) : (
        <form
          className={`contact-form${variant === 'service' ? ' service-form' : ''}`}
          onSubmit={handleSubmit}
          noValidate
        >
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            style={{ position: 'absolute', left: '-9999px' }}
            {...honeypotProps}
          />

          <div className="form-row">
            <Field
              id="cf-name"
              type="text"
              label={t.labels.name}
              placeholder={t.placeholders.name}
              value={formData.name}
              required
              autoComplete="name"
              disabled={isSending}
              invalid={Boolean(errors.name)}
              errorText={t.labels.name}
              onChange={(v) => setField('name', v)}
            />
            <Field
              id="cf-email"
              type="email"
              label={t.labels.email}
              placeholder={t.placeholders.email}
              value={formData.email}
              required
              autoComplete="email"
              disabled={isSending}
              invalid={Boolean(errors.email)}
              errorText={t.labels.email}
              onChange={(v) => setField('email', v)}
            />
          </div>

          <div className="form-row">
            <Field
              id="cf-company"
              type="text"
              label={t.labels.company}
              placeholder={t.placeholders.company}
              value={formData.company}
              required={false}
              autoComplete="organization"
              disabled={isSending}
              invalid={Boolean(errors.company)}
              errorText={t.labels.company}
              onChange={(v) => setField('company', v)}
            />
            <Field
              id="cf-phone"
              type="tel"
              label={t.labels.phone}
              placeholder={t.placeholders.phone}
              value={formData.phone}
              required={false}
              autoComplete="tel"
              disabled={isSending}
              invalid={Boolean(errors.phone)}
              errorText={t.labels.phone}
              onChange={(v) => setField('phone', v)}
            />
          </div>

          <Field
            id="cf-message"
            type="text"
            label={t.labels.message}
            placeholder={t.placeholders.message}
            value={formData.message}
            required
            disabled={isSending}
            invalid={Boolean(errors.message)}
            errorText={t.labels.message}
            onChange={(v) => setField('message', v)}
            multiline
          />

          <div
            className="form-group captcha-wrapper"
            role="group"
            aria-labelledby={captchaGroupLabelId}
          >
            <span id={captchaGroupLabelId} className="captcha-label">
              {t.messages.captchaLabel}
            </span>
            <HCaptcha
              theme="light"
              hl={lang}
              ref={captchaRef}
              sitekey={HCAPTCHA_SITE_KEY}
              reCaptchaCompat={false}
              onVerify={onCaptchaVerify}
              onExpire={onCaptchaExpire}
            />
            {captchaInvalid && (
              <span className="form-field-error" role="alert">
                {t.messages.captchaRequired}
              </span>
            )}
          </div>

          <button
            type="submit"
            className={`form-submit ${isSending ? 'sending' : ''}`}
            disabled={isSending}
          >
            {isSending ? t.messages.submitSending : t.messages.submit}
            {isSending ? null : <IconArrowRight />}
          </button>
        </form>
      )}
    </div>
  )
}

export default ContactForm
